import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChatConversation, ChatMember, ChatMessage, ChatRefCard, ChatRefType, ChatService, ChatStreamEvent } from '../stokker/api/chat.service';
import { avatarColor, firstName, initials, listTime } from './chat-utils';
import { ChatThreadComponent } from './chat-thread.component';

interface ListItem {
  key: string;
  title: string;
  member: ChatMember | null;
  conversation: ChatConversation | null;
  preview: string;
  time: string;
  sort: number;
  unread: number;
  muted: boolean;
}

/**
 * Csapatchat oldal. Asztalon kéthasábos (lista + beszélgetés), telefonon a lista, és a
 * kiválasztott beszélgetés teljes képernyőn nyílik (/chat/fo, /chat/u-12).
 */
@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild(ChatThreadComponent) thread?: ChatThreadComponent;

  me = 0;
  members: ChatMember[] = [];
  conversations: ChatConversation[] = [];
  online = new Set<number>();
  connected = true;
  loading = true;
  error = false;
  search = '';
  activeKey: string | null = null;
  active: ChatConversation | null = null;
  items: ListItem[] = [];

  private subs: Subscription[] = [];
  /** megosztás más oldalról (?ref=order:12) — a kiválasztott beszélgetés üzenetírójába kerül */
  shareCard: ChatRefCard | null = null;

  constructor(private chat: ChatService, private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    this.subs.push(
      this.chat.online$.subscribe((set) => {
        this.online = set;
        this.buildItems();
      }),
      this.chat.connected$.subscribe((c) => (this.connected = c)),
      this.chat.events$.subscribe((e) => this.onEvent(e)),
      this.route.paramMap.subscribe((params) => {
        this.activeKey = params.get('key');
        // mobilon a nyitott beszélgetés alatt az app fejléce / alsó sávja rejtve (lásd styles.scss)
        document.body.classList.toggle('chat-thread-open', !!this.activeKey);
        this.resolveActive();
      }),
      this.route.queryParamMap.subscribe((q) => {
        // megosztás más oldalról: /chat?ref=order:12 → a hivatkozás az üzenetíróba kerül
        const ref = q.get('ref');
        const match = ref ? ref.match(/^(order|ticket|mail):(\d+)$/) : null;
        if (!match) {
          return;
        }
        const id = Number(match[2]);
        this.chat.searchRefs(match[1] as ChatRefType, '', id).subscribe({
          next: (list) => {
            this.shareCard = list.find((c) => c.id === id) || null;
            this.applyShare();
          },
          error: () => (this.shareCard = null),
        });
      }),
    );
    this.load();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('chat-thread-open');
    this.subs.forEach((s) => s.unsubscribe());
    this.chat.setFocus(null);
  }

  load(): void {
    this.loading = true;
    this.error = false;
    this.chat.overview().subscribe({
      next: (o) => {
        this.me = o.me;
        this.members = o.members;
        this.conversations = o.conversations;
        this.loading = false;
        this.buildItems();
        this.resolveActive();
      },
      error: () => {
        this.loading = false;
        this.error = true;
      },
    });
  }

  // ------------------------------------------------------------ lista

  private buildItems(): void {
    if (!this.me) {
      return;
    }
    const main = this.conversations.find((c) => c.type === 'main') || null;
    const items: ListItem[] = [];
    if (main) {
      items.push(this.item('fo', 'Fő chat', null, main));
    }
    const byPeer = new Map(this.conversations.filter((c) => c.type === 'direct').map((c) => [c.peerId as number, c]));
    const pinned: ListItem[] = [];
    this.members
      .filter((m) => m.id !== this.me)
      .forEach((m) => (m.bot ? pinned : items).push(this.item(`u-${m.id}`, m.name, m, byPeer.get(m.id) || null)));
    // a fő chat mindig felül, alatta az MI-kollégák (Foxi, Boris, Farmy), utána a legutóbbi beszélgetések, majd a többiek ábécében
    const [first, ...rest] = items;
    rest.sort((a, b) => b.sort - a.sort || a.title.localeCompare(b.title, 'hu'));
    this.items = first ? [first, ...pinned, ...rest] : [...pinned, ...rest];
  }

  private item(key: string, title: string, member: ChatMember | null, conversation: ChatConversation | null): ListItem {
    const last = conversation?.lastMessage || null;
    let preview = '';
    if (last) {
      const who = last.authorId === this.me ? 'Te' : conversation?.type === 'main' ? firstName(this.nameOf(last.authorId)) : '';
      preview = who ? `${who}: ${last.preview}` : last.preview;
    } else if (member?.bot) {
      preview = /boris/i.test(member.name)
        ? '3D-tervező · küldj vázlatot, rendert 🤓'
        : /farmy/i.test(member.name)
          ? 'Gyártásmérnök · gyártási terv, nyomtatófarm 🏭'
          : 'MI-munkatárs · kérdezz tőle bármit az ERP-ről 🦊';
    } else if (!conversation || conversation.type === 'direct') {
      preview = member && this.online.has(member.id) ? 'Online · írj neki' : 'Még nem beszélgettetek';
    } else {
      preview = 'Még nincs üzenet — köszönj a csapatnak!';
    }
    return {
      key,
      title,
      member,
      conversation,
      preview,
      time: last ? listTime(last.createdAt) : '',
      sort: last ? new Date(last.createdAt).getTime() : 0,
      unread: conversation?.unread || 0,
      muted: !!conversation?.muted,
    };
  }

  get filteredItems(): ListItem[] {
    const q = this.search.trim().toLowerCase();
    return q ? this.items.filter((i) => i.title.toLowerCase().includes(q)) : this.items;
  }

  get onlinePeers(): ChatMember[] {
    return this.members.filter((m) => m.id !== this.me && this.online.has(m.id));
  }

  nameOf(id: number | null): string {
    return this.members.find((m) => m.id === id)?.name || 'Korábbi tag';
  }

  color(name: string): string {
    return avatarColor(name);
  }

  initials(name: string): string {
    return initials(name);
  }

  firstName(name: string): string {
    return firstName(name);
  }

  avatarPath(m: ChatMember | null): string | null {
    return m?.image ? `users/images/${m.image}` : null;
  }

  isOnline(m: ChatMember | null): boolean {
    return !!m && this.online.has(m.id);
  }

  open(item: ListItem): void {
    this.router.navigate(['/chat', item.key], { queryParamsHandling: 'preserve' });
  }

  openMember(m: ChatMember): void {
    this.router.navigate(['/chat', `u-${m.id}`]);
  }

  closeThread(): void {
    this.router.navigate(['/chat']);
  }

  // ------------------------------------------------------------ aktív beszélgetés

  private resolveActive(): void {
    if (!this.me) {
      return;
    }
    const key = this.activeKey;
    if (!key) {
      this.active = null;
      this.chat.setFocus(null);
      return;
    }
    if (key === 'fo') {
      this.setActive(this.conversations.find((c) => c.type === 'main') || null);
      return;
    }
    const peerId = Number(key.replace(/^u-/, ''));
    const existing = this.conversations.find((c) => c.type === 'direct' && c.peerId === peerId);
    if (existing) {
      this.setActive(existing);
      return;
    }
    if (!peerId || !this.members.some((m) => m.id === peerId)) {
      this.router.navigate(['/chat']);
      return;
    }
    // még nincs beszélgetés ezzel a taggal — most jön létre
    this.chat.direct(peerId).subscribe({
      next: (conversation) => {
        if (!this.conversations.some((c) => c.id === conversation.id)) {
          this.conversations = [...this.conversations, conversation];
        }
        this.buildItems();
        if (this.activeKey === key) {
          this.setActive(this.conversations.find((c) => c.id === conversation.id) || conversation);
        }
      },
      error: () => this.router.navigate(['/chat']),
    });
  }

  private setActive(conversation: ChatConversation | null): void {
    if (this.active?.id !== conversation?.id) {
      this.active = conversation;
    }
    setTimeout(() => this.applyShare());
  }

  private applyShare(): void {
    if (!this.shareCard || !this.thread || !this.active) {
      return;
    }
    this.thread.prefillRef(this.shareCard);
    this.shareCard = null;
    this.router.navigate([], { relativeTo: this.route, queryParams: { ref: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  cancelShare(): void {
    this.shareCard = null;
    this.router.navigate([], { relativeTo: this.route, queryParams: { ref: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  onMutedChange(muted: boolean): void {
    if (this.active) {
      this.active.muted = muted;
      this.conversations = this.conversations.map((c) => (c.id === this.active!.id ? { ...c, muted } : c));
      this.buildItems();
    }
  }

  // ------------------------------------------------------------ élő frissítés

  private onEvent(event: ChatStreamEvent): void {
    if (event.type === 'reconnected') {
      this.load();
      return;
    }
    if (event.type === 'presence') {
      this.members = this.members.map((m) => (m.id === event.data.userId ? { ...m, online: event.data.online, lastSeenAt: event.data.lastSeenAt || m.lastSeenAt } : m));
      this.buildItems();
      return;
    }
    if (event.type === 'message' || event.type === 'message_update') {
      const msg = event.data;
      const conv = this.conversations.find((c) => c.id === msg.conversationId);
      if (!conv) {
        // új privát beszélgetés, amit a másik fél indított
        this.load();
        return;
      }
      this.conversations = this.conversations.map((c) => (c.id === msg.conversationId ? this.withMessage(c, msg, event.type === 'message') : c));
      this.syncActive();
      this.buildItems();
      return;
    }
    if (event.type === 'read' && event.data.userId === this.me) {
      this.conversations = this.conversations.map((c) =>
        c.id === event.data.conversationId && (!c.lastMessage || c.lastMessage.id <= event.data.lastReadMessageId) ? { ...c, unread: 0 } : c,
      );
      this.syncActive();
      this.buildItems();
    }
  }

  private withMessage(c: ChatConversation, msg: ChatMessage, isNew: boolean): ChatConversation {
    if (!isNew && c.lastMessage?.id !== msg.id) {
      return c;
    }
    return {
      ...c,
      lastMessage: { id: msg.id, authorId: msg.authorId, preview: this.preview(msg), createdAt: msg.createdAt },
      unread: isNew && msg.authorId !== this.me ? c.unread + 1 : c.unread,
    };
  }

  /** Az aktív beszélgetés objektuma kövesse a listát (a thread csak az id-változásra tölt újra). */
  private syncActive(): void {
    if (this.active) {
      const fresh = this.conversations.find((c) => c.id === this.active!.id);
      if (fresh) {
        Object.assign(this.active, { muted: fresh.muted, unread: fresh.unread, lastMessage: fresh.lastMessage });
      }
    }
  }

  private preview(msg: ChatMessage): string {
    if (msg.deleted) return 'Az üzenetet törölték';
    const text = msg.text.replace(/\s+/g, ' ').trim();
    if (text) return text.slice(0, 140);
    if (msg.attachments.length) {
      const images = msg.attachments.filter((a) => a.isImage).length;
      if (images === msg.attachments.length) return images > 1 ? `📷 ${images} kép` : '📷 Kép';
      return msg.attachments.length > 1 ? `📎 ${msg.attachments.length} fájl` : `📎 ${msg.attachments[0].name}`;
    }
    return msg.refs.length ? '🔗 Hivatkozás' : '';
  }

  trackItem(_i: number, item: ListItem): string {
    return item.key;
  }
}
